import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'
import { formatKwh } from '../relations'
import useDailyUsage, { USAGE_DAYS } from './useDailyUsage'

// Screen-reader-only: the chart's values as a table.
const visuallyHidden = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px'
}

const WIDTH = 300
const HEIGHT = 56
const RADIUS = 2

const dayLabel = (date) => DateTime.fromISO(date).toFormat('ccc d LLL')

/** A column with a rounded top and a square foot, `height` px tall. */
function columnPath(x, width, height) {
    const r = Math.min(RADIUS, height, width / 2)
    const top = HEIGHT - height
    return `M${x},${HEIGHT} V${top + r} Q${x},${top} ${x + r},${top} H${x + width - r} Q${x + width},${top} ${x + width},${top + r} V${HEIGHT} Z`
}

/**
 * The last 30 days of use as a stat tile: total, daily average, how many days
 * have no reading, and a column per day. A day with no reading is a flat tick
 * rather than a zero-height gap nobody notices — missing data is the thing an
 * admin most needs to see here.
 */
export default function UsageSummary({ fuel, meterIds, label }) {
    const { summary, isLoading, isError } = useDailyUsage(fuel, meterIds)

    if (isLoading) {
        return (
            <Skeleton
                variant="rounded"
                height={112}
                aria-label="Loading usage"
            />
        )
    }
    if (isError) {
        return (
            <Typography variant="body2" color="text.secondary">
                Usage couldn’t be loaded.
            </Typography>
        )
    }

    const { days, total, average, missingDays, max } = summary
    const band = WIDTH / days.length
    const barWidth = Math.min(band - 2, 24)
    const noData = missingDays === days.length

    return (
        <Box>
            <Typography variant="body2" color="text.secondary">
                {label} · last {USAGE_DAYS} days
            </Typography>
            <Stack
                direction="row"
                spacing={2}
                alignItems="baseline"
                sx={{ mt: 0.25 }}
            >
                <Typography variant="h6" component="p">
                    {noData ? 'No readings' : formatKwh(total)}
                </Typography>
                {average != null ? (
                    <Typography variant="body2" color="text.secondary">
                        {formatKwh(average)} a day on average
                    </Typography>
                ) : null}
            </Stack>
            {missingDays > 0 && !noData ? (
                <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ mt: 0.5 }}
                >
                    <WarningAmberIcon color="warning" sx={{ fontSize: 16 }} />
                    <Typography variant="body2" color="text.secondary">
                        {missingDays}{' '}
                        {missingDays === 1 ? 'day has' : 'days have'} no reading
                    </Typography>
                </Stack>
            ) : null}
            <Box
                component="svg"
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                preserveAspectRatio="none"
                aria-hidden="true"
                sx={{
                    display: 'block',
                    width: '100%',
                    height: HEIGHT,
                    mt: 1.5,
                    overflow: 'visible'
                }}
            >
                <Box
                    component="line"
                    x1={0}
                    x2={WIDTH}
                    y1={HEIGHT}
                    y2={HEIGHT}
                    sx={{
                        stroke: (theme) => theme.palette.divider,
                        strokeWidth: 1
                    }}
                />
                {days.map((day, index) => {
                    const x = index * band + (band - barWidth) / 2
                    const height =
                        day.value == null || max <= 0
                            ? 0
                            : Math.max(2, (day.value / max) * (HEIGHT - 2))
                    return (
                        <Tooltip
                            key={day.date}
                            title={`${dayLabel(day.date)}: ${day.value == null ? 'no reading' : formatKwh(day.value)}`}
                            placement="top"
                            disableInteractive
                        >
                            <Box
                                component="g"
                                sx={{
                                    '&:hover .usage-mark': {
                                        fill: (theme) =>
                                            theme.palette.primary.dark
                                    }
                                }}
                            >
                                {/* Hit target: the whole column, not just the bar. */}
                                <rect
                                    x={index * band}
                                    y={0}
                                    width={band}
                                    height={HEIGHT}
                                    fill="transparent"
                                />
                                {day.value == null ? (
                                    <Box
                                        component="rect"
                                        x={x}
                                        y={HEIGHT - 2}
                                        width={barWidth}
                                        height={2}
                                        sx={{
                                            fill: (theme) =>
                                                theme.palette.text.disabled
                                        }}
                                    />
                                ) : (
                                    <Box
                                        component="path"
                                        className="usage-mark"
                                        d={columnPath(x, barWidth, height)}
                                        sx={{
                                            fill: (theme) =>
                                                theme.palette.primary.main
                                        }}
                                    />
                                )}
                            </Box>
                        </Tooltip>
                    )
                })}
            </Box>
            <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 0.5 }}
            >
                <Typography variant="caption" color="text.secondary">
                    {dayLabel(days[0].date)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {dayLabel(days[days.length - 1].date)}
                </Typography>
            </Stack>
            <Box component="table" sx={visuallyHidden}>
                <caption>
                    {label}, daily use over the last {USAGE_DAYS} days
                </caption>
                <tbody>
                    {days.map((day) => (
                        <tr key={day.date}>
                            <th scope="row">{dayLabel(day.date)}</th>
                            <td>
                                {day.value == null
                                    ? 'No reading'
                                    : formatKwh(day.value)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Box>
        </Box>
    )
}

UsageSummary.propTypes = {
    fuel: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    meterIds: PropTypes.array.isRequired
}
